/**
 * Implementation of a Duration spinner.
 *
 * Copyright © Ircam amplify 2024
 */
import { useEffect, useState } from 'react'
import { logger } from '@/config/logger'
import { Util } from '@amplify3/duplo-back-end'

interface DurationSpinnerProps {
  setConfigParam: (paramId: string, value: any) => void
  param: any
  sourceDuration: number
  handleChange: (e: any) => void
  id?: string
}

const DurationSpinner = (props: DurationSpinnerProps) => {
  const [maxValue, setMaxValue] = useState<number>(0)
  const [inputValue, setInputValue] = useState<number | undefined>(0)
  const [configParamValue, setConfigParamValue] = useState(0)

  /**
   * Update the max value of the component and the initial value.
   */
  useEffect(() => {
    logger.debug(`sourceDuration: ${props.sourceDuration}`)
    // Max value
    setMaxValue(Math.trunc(props.sourceDuration))

    // Initial value
    if (0 !== props.sourceDuration && 0 === inputValue) {
      const value1 = 0.5 * props.sourceDuration + 15
      const value2 = props.sourceDuration - 60
      const value = Math.trunc(Math.max(value1, value2))
      setInputValue(value)
      setConfigParamValue(value)
      props.setConfigParam(props.param.name, value)
    }
  }, [props.sourceDuration])

  // Debug logs
  useEffect(() => {
    logger.debug(`param.min: ${props.param.min}`)
  }, [props.param])
  useEffect(() => {
    logger.debug(`inputValue: ${inputValue}`)
  }, [inputValue])

  // TODO: why "optimization" parameter is managed in this duration component?
  // This should be removed, or moved to another component.
  useEffect(() => {
    // Send the default value "Quality" of the optimization at the beginning
    // of the componenent display.
    props.handleChange({ target: { name: 'optimization', value: 'quality' } })
  }, [])

  /**
   * Return true if the given input event is an input from keyboard typing.
   * @param event The event.
   */
  const isKeyboardTyping = (event: any) => {
    switch (event.nativeEvent.inputType) {
      case 'insertText':
      case 'deleteContentBackward':
        return true

      default:
        return false
    }
  }

  /**
   * Handle key pressed.
   */
  const onKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      // Validate the input value
      onChange(event)
    } else if (!Util.isNullOrEmpty(event.target.value)) {
      // Just print the value in the component
      const value = event.target.value
      setInputValue(Number(value))
    }
  }

  /**
   * Handle change of value.
   */
  const onChange = (event: any) => {
    const { name, value } = event.target
    const data = Number(value)

    if (maxValue) {
      const fixedValue =
        data < props.param.min
          ? props.param.min
          : data > maxValue
            ? maxValue
            : data

      logger.debug(`Value: ${data}, param value to set: ${fixedValue}`)
      if (fixedValue != configParamValue) {
        setConfigParamValue(fixedValue)
        props.setConfigParam(name, fixedValue)
      }
      setInputValue(fixedValue)
    }
  }

  /**
   * Handle any direct input (from up/down buttons or typing)
   */
  const handleInput = (event: any) => {
    if (isKeyboardTyping(event)) {
      // Input value changed by keyboard typing
      const value = event.target.value

      if (value !== '') {
        // Update the inputValue state to display the new number
        setInputValue(Number(value))
      } else {
        // Number completely deleted: clear the component.
        setInputValue(undefined)
      }
    } else {
      // Input value changed by up/down buttons.
      onChange(event)
    }
  }

  return (
    <div>
      <input
        value={inputValue}
        type="number"
        name={props.param.name}
        id={props.id}
        onBlur={onChange} // Handle loss of focus
        onKeyDown={onKeyDown} // Detect Enter key press
        onInput={handleInput} // Allow changes from up/down buttons
        min={props.param.min}
        max={maxValue}
        step={props.param.step}
        disabled={props.sourceDuration < props.param.min}
        className={
          props.sourceDuration < props.param.min
            ? 'cursor-not-allowed'
            : 'cursor-auto'
        }
      />{' '}
      {props.param.unit}
      {maxValue > 0 && ` (max: ${maxValue} s)`}
    </div>
  )
}

export default DurationSpinner
